// Human-readable presentation of a CoachProposal for the Apply card.
//
// The coach applies changes to the persona DRAFT, but a proposal card that
// shows only the coach's rationale gives the owner no way to see WHAT is
// changing or WHERE — a reviewability gap (staging 2026-07-01: an applied
// `updateBlock('trait', …)` landed in the "Personality traits" field, saved
// fine, but looked like a no-op because the card never named the field or
// showed the new text). These pure helpers turn a proposal into a target
// label + new content so the card can render a before→after the owner can
// actually review.
import type { CoachProposal } from '@/types/personaCoach'
import type { BlockCategory } from '@/stores/composerStore'
import type { SkillCatalogEntry } from '@/hooks/useSkillCatalog'
import type { WorkflowSummary } from '@/hooks/useWorkflows'
import { BLOCK_TAXONOMY } from './blockTaxonomy'

/**
 * Field label per block category, projected from the single taxonomy source so
 * the coach proposal card and the Workshop fields can never drift.
 */
export const BLOCK_FIELD_LABELS: Record<BlockCategory, string> = Object.fromEntries(
  (Object.keys(BLOCK_TAXONOMY) as BlockCategory[]).map((c) => [c, BLOCK_TAXONOMY[c].label]),
) as Record<BlockCategory, string>

export interface ProposalCatalogs {
  skills: SkillCatalogEntry[]
  workflows: WorkflowSummary[]
}

/** Human-readable "what & where" for a proposal — the card's heading. */
export function proposalTargetLabel(p: CoachProposal, cats: ProposalCatalogs): string {
  switch (p.op) {
    case 'updateBlock':
      return BLOCK_FIELD_LABELS[p.category] ?? p.category
    case 'addSkill': {
      const s = cats.skills.find((x) => x.name === p.name)
      return `Add skill: ${s?.display_name ?? p.name}`
    }
    case 'removeSkill': {
      const s = cats.skills.find((x) => x.name === p.name)
      return `Remove skill: ${s?.display_name ?? p.name}`
    }
    case 'installWorkflow': {
      const w = cats.workflows.find((x) => x.id === p.workflow_id)
      return `Install workflow: ${w?.name ?? p.workflow_id}`
    }
    case 'removeWorkflow': {
      const w = cats.workflows.find((x) => x.id === p.workflow_id)
      return `Remove workflow: ${w?.name ?? p.workflow_id}`
    }
    default:
      return 'Change'
  }
}

/** The proposed new text for a block edit (for before→after display); else null. */
export function proposalNewContent(p: CoachProposal): string | null {
  return p.op === 'updateBlock' ? p.content : null
}
